import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, History, Lock, Unlock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { CollapsibleCard } from "@/components/cinch/CollapsibleCard";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  monthLabel,
  useReconPeriod,
  useReopen,
  useSignOff,
  useUnlockStep,
  useUpdateStep,
  type PeriodDetail,
  type StepState,
  type StepStatus,
} from "../api";
import { BilledActualsPanel } from "../components/BilledActualsPanel";
import { BillingStartPanel } from "../components/BillingStartPanel";
import { DirectChurnPanel } from "../components/DirectChurnPanel";
import { GapAnalysisPanel } from "../components/GapAnalysisPanel";
import { IntegrationExpensePanel } from "../components/IntegrationExpensePanel";
import { BillingValidationPanel } from "../components/BillingValidationPanel";
import { ReplacementSuppressionPanel } from "../components/ReplacementSuppressionPanel";
import { InternalAccountsPanel } from "../components/InternalAccountsPanel";
import { ParentChildPanel } from "../components/ParentChildPanel";
import { SignedContractsPanel } from "../components/SignedContractsPanel";
import {
  RECON_ROLE_LABEL,
  STEP_CONTENT,
  modeLabel,
  stepDisplayNo,
  stepStatusChip,
} from "../steps";

const STATUS_OPTIONS: { value: StepStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In progress" },
  { value: "blocked", label: "Blocked" },
  { value: "complete", label: "Complete" },
  { value: "not_applicable", label: "Not applicable" },
];

export function ReconciliationStepPage() {
  const { period: rp, stepKey: sk } = useParams({ strict: false }) as {
    period?: string;
    stepKey?: string;
  };
  const period = rp ?? "";
  const stepKey = sk ?? "";
  const { data, isLoading } = useReconPeriod(period);
  const step = data?.steps.find((s) => s.step_key === stepKey);
  const content = STEP_CONTENT[stepKey];
  const isSignOff = stepKey === "R-11";

  // Assignment-scoped users get a filtered step list — a step that isn't in
  // it is off-limits, not merely empty.
  if (data && !step) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to="/reconciliation/$period"
          params={{ period }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {monthLabel(period)} close
        </Link>
        <Card>
          <CardContent className="flex items-start gap-2 py-6 text-sm text-muted-foreground">
            <Lock className="mt-px h-4 w-4 flex-shrink-0" />
            <span>
              This step isn't part of your assignments. Ask a reconciliation
              admin for access if you need to work it.
            </span>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link
          to="/reconciliation/$period"
          params={{ period }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {monthLabel(period)} close
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              {/* The number is the step's POSITION, which only the loaded
                  step knows — the key digits are NOT it (keys never
                  renumber), so show a dash until the period resolves. */}
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-power-light text-sm font-bold text-power">
                {step ? stepDisplayNo(step.order) : "—"}
              </span>
              <h1 className="text-2xl font-bold tracking-tight">
                {step?.title ?? stepKey}
              </h1>
              {content?.toBuild && <Badge variant="info">To build</Badge>}
            </div>
            {step && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {modeLabel(step.mode)} · Owner:{" "}
                {step.owner_roles
                  .map(
                    (r) =>
                      `${RECON_ROLE_LABEL[r]?.short ?? r} (${
                        RECON_ROLE_LABEL[r]?.person ?? "—"
                      })`,
                  )
                  .join(", ")}
              </p>
            )}
          </div>
          {step && <StatusBadge {...stepStatusChip(step.status)} />}
        </div>
      </div>

      {/* Description (from the playbook). Collapsed by default — it's reference
          material, useful the first few times you work a step and pure scroll
          tax after that, and expanded it pushed the actual worklist below the
          fold on every one of the 21 steps. The tagline rides in the header so
          the collapsed row still says what the step is for. */}
      <CollapsibleCard title="What this step does" meta={content?.tagline}>
        {content?.tagline && <p className="text-sm">{content.tagline}</p>}
        {content && (
          <dl className="divide-y divide-border rounded-md border">
            {content.detail.map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-[104px_1fr] gap-3 px-4 py-3"
              >
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {row.label}
                </dt>
                <dd className="text-sm leading-relaxed">{row.body}</dd>
              </div>
            ))}
          </dl>
        )}
      </CollapsibleCard>

      {/* Step data — wired worklists per step; the rest are placeholders
          until they're built out one step at a time. */}
      {stepKey === "R-01" ? (
        <BilledActualsPanel period={period} />
      ) : stepKey === "R-14" ? (
        <SignedContractsPanel period={period} />
      ) : stepKey === "R-15" ? (
        <BillingValidationPanel period={period} />
      ) : stepKey === "R-08" ? (
        <ReplacementSuppressionPanel period={period} />
      ) : stepKey === "R-02" ? (
        <BillingStartPanel period={period} />
      ) : stepKey === "R-03" ? (
        <GapAnalysisPanel period={period} />
      ) : stepKey === "R-04" ? (
        <DirectChurnPanel period={period} />
      ) : stepKey === "R-16" ? (
        <ParentChildPanel period={period} />
      ) : stepKey === "R-17" ? (
        <InternalAccountsPanel period={period} />
      ) : stepKey === "R-18" ? (
        <IntegrationExpensePanel period={period} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Step data</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border border-dashed bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
              Live data for this step will appear here. It will be computed from Snowflake
              (BI.REVENUE_WATERFALL · BILLING_CONTRACTS) and the contract store, then
              migrated to the planned architecture — wired up one step at a time.
            </div>
            {step && step.data && Object.keys(step.data).length > 0 && (
              <pre className="mt-3 overflow-auto rounded-md bg-muted p-3 text-xs">
                {JSON.stringify(step.data, null, 2)}
              </pre>
            )}
          </CardContent>
        </Card>
      )}

      {/* Input / sign-off */}
      {isSignOff ? (
        <SignOffPanel period={period} data={data ?? null} loading={isLoading} />
      ) : (
        <StepInputPanel period={period} step={step} loading={isLoading} />
      )}
    </div>
  );
}

function StepAuditTrail({ step }: { step: StepState }) {
  if (step.events.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <History className="h-3.5 w-3.5" /> Sign-off history
      </span>
      <ol className="divide-y divide-border rounded-md border text-xs">
        {step.events.map((ev, i) => (
          <li key={i} className="flex flex-wrap items-baseline gap-x-2 px-3 py-2">
            <span
              className={
                ev.action === "completed"
                  ? "font-semibold text-success"
                  : "font-semibold text-warning"
              }
            >
              {ev.action === "completed" ? "Signed off" : "Unlocked"}
            </span>
            <span>by {ev.actor_name ?? "—"}</span>
            <span className="text-muted-foreground">
              {new Date(ev.at).toLocaleString()}
            </span>
            {ev.note && (
              <span className="basis-full text-muted-foreground">“{ev.note}”</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function UnlockDialog({
  period,
  step,
  open,
  onOpenChange,
}: {
  period: string;
  step: StepState;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const unlock = useUnlockStep(period);
  const [note, setNote] = useState("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unlock {step.step_key}?</DialogTitle>
          <DialogDescription>
            This reopens a signed-off step so its data can change. The reason is
            required and goes in the sign-off history, along with who unlocked
            it and when it's signed off again.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          aria-label="Unlock reason"
          placeholder="Why is this step being unlocked?"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              unlock.mutate(
                { stepKey: step.step_key, note: note.trim() },
                {
                  onSuccess: () => {
                    toast.success(`${step.step_key} unlocked`);
                    setNote("");
                    onOpenChange(false);
                  },
                  onError: (e) =>
                    toast.error("Couldn't unlock", {
                      description: (e as Error).message,
                    }),
                },
              )
            }
            disabled={note.trim().length < 3 || unlock.isPending}
          >
            <Unlock className="mr-1.5 h-4 w-4" />
            {unlock.isPending ? "Unlocking…" : "Unlock step"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepInputPanel({
  period,
  step,
  loading,
}: {
  period: string;
  step: StepState | undefined;
  loading: boolean;
}) {
  const update = useUpdateStep(period);
  const [status, setStatus] = useState<StepStatus>(step?.status ?? "pending");
  const [notes, setNotes] = useState(step?.notes ?? "");
  const [unlockOpen, setUnlockOpen] = useState(false);

  // Seed the form from the loaded step once, and re-seed only when navigating
  // to a different step OR when the lock state flips (unlock resets the form).
  const seededKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    const key = step ? `${step.step_key}:${step.locked}` : undefined;
    if (step && key !== seededKey.current) {
      seededKey.current = key;
      setStatus(step.status);
      setNotes(step.notes ?? "");
    }
  }, [step]);

  if (loading && !step) return <Card className="h-40 animate-pulse" />;

  const canAct = step?.can_act ?? false;
  const dirty =
    !!step && (status !== step.status || (notes ?? "") !== (step.notes ?? ""));

  // Complete == locked: show the sign-off record + admin unlock, no editors.
  if (step?.locked) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sign-off</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-success/40 bg-success-light/50 px-3 py-2.5 text-sm text-success">
            <CheckCircle2 className="mt-px h-4 w-4 flex-shrink-0" />
            <span>
              Signed off by <b>{step.completed_by_name ?? "—"}</b>
              {step.completed_at
                ? ` on ${new Date(step.completed_at).toLocaleString()}`
                : ""}
              . The step is locked — its data and status can't change.
            </span>
          </div>
          {step.notes && (
            <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
              {step.notes}
            </p>
          )}
          <StepAuditTrail step={step} />
          {step.can_unlock ? (
            <Button variant="outline" onClick={() => setUnlockOpen(true)}>
              <Unlock className="mr-1.5 h-4 w-4" /> Unlock…
            </Button>
          ) : (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5" /> Only a reconciliation admin can
              unlock a completed step.
            </p>
          )}
          <UnlockDialog
            period={period}
            step={step}
            open={unlockOpen}
            onOpenChange={setUnlockOpen}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Your input</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {step && !canAct && (
          <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <Lock className="mt-px h-3.5 w-3.5 flex-shrink-0" />
            <span>
              This step is owned by{" "}
              {step.owner_roles
                .map((r) => RECON_ROLE_LABEL[r]?.short ?? r)
                .join(", ")}
              . You can view it, but only an owner can record progress.
            </span>
          </div>
        )}

        <div className="grid gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Status
          </span>
          <Select
            value={status}
            onValueChange={(v) => setStatus(v as StepStatus)}
            disabled={!canAct}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Notes
          </span>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!canAct}
            rows={4}
            aria-label="Step notes"
            placeholder="Notes, findings, or context for this step…"
          />
        </div>

        {step && <StepAuditTrail step={step} />}

        {status === "complete" && (
          <p className="text-xs text-muted-foreground">
            Saving as <b>Complete</b> signs the step off in your name and locks
            it — further changes need an admin unlock.
          </p>
        )}

        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              if (!step) return;
              update.mutate(
                { stepKey: step.step_key, update: { status, notes } },
                {
                  onSuccess: () => toast.success(`${step.step_key} updated`),
                  onError: (e) =>
                    toast.error("Couldn't save step", {
                      description: (e as Error).message,
                    }),
                },
              );
            }}
            disabled={!canAct || !dirty || update.isPending}
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SignOffPanel({
  period,
  data,
  loading,
}: {
  period: string;
  data: PeriodDetail | null;
  loading: boolean;
}) {
  const signOff = useSignOff(period);
  const reopen = useReopen(period);
  const [notes, setNotes] = useState("");

  if (loading && !data) return <Card className="h-40 animate-pulse" />;

  const canSign = data?.can_sign_off ?? false;
  const signedOff = data?.status === "signed_off";
  const done = data?.steps_done ?? 0;
  const total = data?.steps_total ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Sign off the close</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canSign && (
          <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <Lock className="mt-px h-3.5 w-3.5 flex-shrink-0" />
            <span>Only an approver (CRO / CFO / CEO) can sign off the month.</span>
          </div>
        )}

        {signedOff ? (
          <div className="flex items-start gap-2 rounded-md border border-success/40 bg-success-light/50 px-3 py-2.5 text-sm text-success">
            <CheckCircle2 className="mt-px h-4 w-4 flex-shrink-0" />
            <span>
              Signed off by {data?.signed_off_by_name ?? "—"}
              {data?.signed_off_at
                ? ` on ${new Date(data.signed_off_at).toLocaleString()}`
                : ""}
              .
            </span>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {done} of {total} steps complete. Signing off marks the month's
            reconciliation done and publishes the numbers.
          </p>
        )}

        {!signedOff && canSign && (
          <>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              aria-label="Sign-off note"
              placeholder="Optional sign-off note…"
            />
            <Button
              onClick={() =>
                signOff.mutate(notes || undefined, {
                  onSuccess: () => toast.success("Close signed off"),
                  onError: (e) =>
                    toast.error("Couldn't sign off", {
                      description: (e as Error).message,
                    }),
                })
              }
              disabled={signOff.isPending}
            >
              <CheckCircle2 className="mr-1.5 h-4 w-4" />
              {signOff.isPending ? "Signing off…" : "Sign off"}
            </Button>
          </>
        )}

        {signedOff && canSign && (
          <Button
            variant="outline"
            onClick={() =>
              reopen.mutate(undefined, {
                onSuccess: () => toast.success("Close reopened"),
                onError: (e) =>
                  toast.error("Couldn't reopen", { description: (e as Error).message }),
              })
            }
            disabled={reopen.isPending}
          >
            {reopen.isPending ? "Reopening…" : "Reopen close"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
